import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import PropTypes from "prop-types";

const TRANSFORM_TYPES = [
  { value: "", label: "Keine" },
  { value: "trim", label: "Trim" },
  { value: "uppercase", label: "Großschreibung" },
  { value: "lowercase", label: "Kleinschreibung" },
  { value: "date", label: "Datum" },
  { value: "default", label: "Standardwert" },
  { value: "map", label: "Wertzuordnung (map)" },
  { value: "split", label: "Aufteilen (split)" },
  { value: "concat", label: "Verketten (concat)" },
  { value: "regex_extract", label: "Regex-Extraktion" },
  { value: "currency", label: "Währung (DE)" },
];

// ---------------------------------------------------------------------------
// Map params editor: { values: { "A": "active", ... } }
// ---------------------------------------------------------------------------
function MapParamsEditor({ params, onChange }) {
  const values = params.values ?? {};
  const pairs = Object.entries(values);

  function addPair() {
    onChange({ ...params, values: { ...values, "": "" } });
  }

  function removePair(from_key) {
    const next = { ...values };
    delete next[from_key];
    onChange({ ...params, values: next });
  }

  function updateKey(old_key, new_key) {
    const next = {};
    for (const [k, v] of Object.entries(values)) {
      next[k === old_key ? new_key : k] = v;
    }
    onChange({ ...params, values: next });
  }

  function updateValue(from_key, new_value) {
    onChange({ ...params, values: { ...values, [from_key]: new_value } });
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        Quellwert → Zielwert
      </Typography>
      {pairs.map(([from_key, to_val], i) => (
        <Box key={i} display="flex" gap={0.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Quellwert"
            value={from_key}
            onChange={(e) => updateKey(from_key, e.target.value)}
            sx={{ width: 110 }}
          />
          <Typography variant="body2" sx={{ px: 0.5 }}>
            →
          </Typography>
          <TextField
            size="small"
            placeholder="Zielwert"
            value={to_val}
            onChange={(e) => updateValue(from_key, e.target.value)}
            sx={{ width: 110 }}
          />
          <IconButton size="small" onClick={() => removePair(from_key)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box>
        <IconButton size="small" onClick={addPair} title="Zuordnung hinzufügen">
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Concat params editor: { parts: [{ kind: "field"|"literal", value: "..." }] }
// ---------------------------------------------------------------------------
function ConcatParamsEditor({ params, onChange }) {
  const parts = params.parts ?? [];

  function addPart(kind) {
    onChange({ ...params, parts: [...parts, { kind, value: "" }] });
  }

  function removePart(index) {
    onChange({ ...params, parts: parts.filter((_, i) => i !== index) });
  }

  function updatePart(index, key, value) {
    const next = parts.map((p, i) =>
      i === index ? { ...p, [key]: value } : p
    );
    onChange({ ...params, parts: next });
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        Teile (Quellspalten oder feste Texte)
      </Typography>
      {parts.map((part, i) => (
        <Box key={i} display="flex" gap={0.5} alignItems="center">
          <FormControl size="small" sx={{ width: 100 }}>
            <Select
              value={part.kind}
              onChange={(e) => updatePart(i, "kind", e.target.value)}
            >
              <MenuItem value="field">Spalte</MenuItem>
              <MenuItem value="literal">Text</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder={part.kind === "field" ? "Spaltenname" : "Fester Text"}
            value={part.value}
            onChange={(e) => updatePart(i, "value", e.target.value)}
            sx={{ width: 140 }}
          />
          <IconButton size="small" onClick={() => removePart(i)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box display="flex" gap={0.5}>
        <IconButton
          size="small"
          onClick={() => addPart("field")}
          title="Quellspalte hinzufügen"
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main TransformEditor
// ---------------------------------------------------------------------------
export default function TransformEditor({ transform, onChange }) {
  const type = transform?.type ?? "";
  const params = transform?.params ?? {};

  function handleTypeChange(new_type) {
    if (!new_type) {
      onChange(null);
    } else if (new_type === "currency") {
      onChange({ type: new_type, params: { format: "de" } });
    } else {
      onChange({ type: new_type, params: {} });
    }
  }

  function handleParamChange(key, value) {
    onChange({ type, params: { ...params, [key]: value } });
  }

  return (
    <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Transform</InputLabel>
        <Select
          value={type}
          label="Transform"
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {TRANSFORM_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {type === "date" && (
        <TextField
          size="small"
          label="Format"
          placeholder="d.m.Y"
          value={params.format ?? ""}
          onChange={(e) => handleParamChange("format", e.target.value)}
          sx={{ width: 120 }}
        />
      )}

      {type === "default" && (
        <TextField
          size="small"
          label="Wert"
          value={params.value ?? ""}
          onChange={(e) => handleParamChange("value", e.target.value)}
          sx={{ width: 160 }}
        />
      )}

      {type === "split" && (
        <>
          <TextField
            size="small"
            label="Trennzeichen"
            value={params.delimiter ?? ""}
            onChange={(e) => handleParamChange("delimiter", e.target.value)}
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            label="Index"
            type="number"
            value={params.index ?? 0}
            onChange={(e) =>
              handleParamChange("index", parseInt(e.target.value, 10))
            }
            sx={{ width: 80 }}
          />
        </>
      )}

      {type === "regex_extract" && (
        <TextField
          size="small"
          label="Muster"
          placeholder="(\d+)"
          value={params.pattern ?? ""}
          onChange={(e) => handleParamChange("pattern", e.target.value)}
          sx={{ width: 180 }}
        />
      )}

      {type === "map" && (
        <MapParamsEditor
          params={params}
          onChange={(updated_params) =>
            onChange({ type, params: updated_params })
          }
        />
      )}

      {type === "concat" && (
        <ConcatParamsEditor
          params={params}
          onChange={(updated_params) =>
            onChange({ type, params: updated_params })
          }
        />
      )}
    </Box>
  );
}

TransformEditor.propTypes = {
  transform: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};
